<template>
  <component
    :is="lazyLoad ? 'div' : 'CCard'"
    v-if="lazyLoad || (creatives && creatives.length)"
  >
    <CCardHeader v-if="!lazyLoad">
      Creatives
      <CIcon
        v-c-tooltip="'Displays a list of seller creatives'"
        name="cil-basket"
        size="custom"
      />
    </CCardHeader>
    <component :is="lazyLoad ? 'div' : 'CCardBody'">
      <slot name="form" />
      <div
        v-if="isLoading"
        :style="{ height: '80vh' }"
        class="d-flex flex-column justify-content-center align-items-center"
      >
        <CSpinner
          class="custom-spinner"
          color="info"
          grow
        />
      </div>
      <div v-else>
        <CDataTable
          data-cy="table-creatives"
          small
          border
          sorter
          column-filter
          :sorter-value="{ column: 'name', asc: true }"
          :items="formattedCreatives"
          :fields="fields"
          :items-per-page="10"
          :pagination="{align: 'center'}"
        >                    
          <template #stats="{item}">
            <td>
              <div
                v-if="item.stats"
                class="w-fixed-130"
              >
                <div>Total: {{ getSafe(() => item.stats.total) }}</div>
                <div>Pending: {{ getSafe(() => item.stats.pending) }}</div>
                <div>Approved: {{ getSafe(() => item.stats.approved) }}</div>
                <div>Rejected: {{ getSafe(() => item.stats.rejected) }}</div>
              </div>
            </td>
          </template>

          <template #id="{item}">
            <td>
              <router-link
                :to="{ name: 'CreativeDetails', params: { id: getSafe(() => item.id)} }"
                target="_blank"                 
              >
                {{ getSafe(() => item.id) }}
              </router-link>

              <a
                href
                class="ml-1"
                @click.prevent="
                  copyIdToClipboard(getSafe(() => item.id))
                "
              ><CIcon
                name="cil-copy"
              /></a>            
            </td>
          </template>
        </CDataTable>        
      </div>
    </component>
  </component>
</template>

<script>
import { mapActions } from 'vuex'
import { dateTimeFormat } from '@/utils'
import { showSuccessMessage } from '@/notification-utils'
import copyToClipboardMixin from '@/mixins/copy-to-clipboard-mixin'
import CreativeApi from '@/api/creative.api'

export default {
  name: 'SellerCreatives',

  mixins: [copyToClipboardMixin],

  props: {
    accountId: {
      type: String,
      default: ''
    },

    lazyLoad: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {    
      isLoading: false,
      creatives: [],  
      fields: [
        { key: 'id', label: 'ID', sortable: true },
        { key: 'externalId', label: 'External ID', sortable: true },        
        { key: 'categoryName', label: 'Category', sortable: true },               
        { key: 'trafficSourceName', label: 'Traffic Source', sortable: true },               
        { key: 'countryId', label: 'Country', sortable: true },               
        { key: 'languageId', label: 'Language', sortable: true },               
        { key: 'status', label: 'Status', sortable: true },          
        { key: 'enabled', label: 'Enabled', sortable: true },          
        { key: 'marketCategory', label: 'Type', sortable: true },          
        { key: 'stats', label: 'Buyer Approval Statistics', filter: false, sorter: false },               
        { key: 'formattedCreationDate', label: 'Created At', sorter: true }, 
      ],
    }
  },  

  computed: { 
    formattedCreatives() {
      return this.creatives        
        .map(creative => {
          return {
            ...creative,
            formattedCreationDate: creative.creationDate ? dateTimeFormat.format(new Date(creative.creationDate)) : '',
            accountName: this.getSafe(() => creative.account.name),
            categoryName: 'Health - Medicare',
            trafficSourceName: this.getSafe(() => creative.trafficSource.name),
            countryId: this.getSafe(() => creative.country.id),
            languageId: this.getSafe(() => creative.language.id),
          }
        })
    }, 
  },
  
  async created () {
    if (!this.lazyLoad) {
      await this.loadCreatives()
    }
  },

  methods: {
    ...mapActions('creative', [
      'getSellerCreatives'
    ]),

    async loadCreatives() {
      try {
        this.isLoading = true
        const response = await CreativeApi.getSellerCreatives(this.accountId)
        this.creatives = response.data
      } catch (error) {
        //
      } finally {
        this.isLoading = false
      }
    },
    
    copyIdToClipboard(id) {
      this.copyTextToClipboard(id)
      showSuccessMessage('Creative ID copied to clipboard')
    }  
  },
}
</script>

<style>

</style>